import {
  DEFAULT_LANGUAGE, JOURNEY_COMPLETE_BONUS, applyDailyOutcome, applyJourneyOutcome, challengeXp,
  dailyXp, journeyCompletedBy, journeyXp, loadProfile, loadStats, recordGame, saveProfile,
  saveStats, type GameState, type JourneyState, type Language, type StorageAdapter,
} from '@lexicon/core';
import { loadJourney, saveJourney } from './journeyStore';
import { loadSession, saveSession, toSession, type GameResult, type GameSession } from './session';

export interface FinalizeArgs {
  storage: StorageAdapter;
  /** `String(dayNumber)` for dailies, the challenge code for challenges. */
  id: string;
  /** A finished game state (status `won` or `lost`). */
  state: GameState;
  /** Required for daily games; ignored for challenges. */
  dayNumber?: number;
  /**
   * The language this game was played in. Decides which profile, stats,
   * journey and session the award lands in — a Dutch win must never
   * advance an English streak (spec §10.2). Defaults to English so every
   * pre-Dutch call site means what it always did.
   */
  language?: Language;
  /** Injectable clock for tests. */
  now?: () => number;
}

/**
 * Finalizations already running, keyed `mode:id`. React StrictMode invokes
 * effects twice in the same tick, so the async "already has a result" check
 * alone is not enough — both calls would read "no result" before either
 * writes one. This dedupes repeat calls for the *same* game.
 */
const inFlight = new Map<string, Promise<GameSession>>();

/**
 * Every finalization's read-modify-write of the shared profile/stats blobs
 * is chained onto this promise, regardless of which game it's for. The
 * `inFlight` map above only protects a single game against itself — two
 * *different* games (e.g. a daily and a challenge) finalizing in the same
 * tick have no such protection, and would otherwise interleave their reads
 * and writes of `KEYS.profile`/`KEYS.stats` and silently drop one game's
 * XP, streak, and stats update. Chaining every call through one tail
 * promise serializes them: the next finalization's work does not start
 * until the previous one's `saveProfile`/`saveStats`/`saveSession` has
 * fully completed.
 */
let tail: Promise<unknown> = Promise.resolve();

/** Run `work` after every previously enqueued finalization has settled. */
function enqueue<T>(work: () => Promise<T>): Promise<T> {
  const result = tail.then(work, work);
  // A failed finalization must not wedge the queue for the next game.
  tail = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

/**
 * Award XP, advance the streak, record stats and persist the finished
 * session — exactly once per game.
 *
 * Idempotency has two layers: the in-flight map above (same tick, same
 * game) and the persisted session's `result` (remount, reload, second
 * visit). Cross-game safety is a third, separate property, provided by the
 * `tail` queue above.
 */
export function finalizeGame(args: FinalizeArgs): Promise<GameSession> {
  // The language is part of the identity: the same day number in two
  // languages is two different games, and neither may dedupe the other.
  const key = `${args.language ?? DEFAULT_LANGUAGE}:${args.state.mode}:${args.id}`;
  const pending = inFlight.get(key);
  if (pending) return pending;
  const run = enqueue(() => finalizeOnce(args)).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, run);
  return run;
}

async function finalizeOnce(args: FinalizeArgs): Promise<GameSession> {
  const { storage, id, state, dayNumber, language = DEFAULT_LANGUAGE, now = Date.now } = args;
  if (state.status === 'playing') {
    throw new Error('finalizeGame requires a finished game');
  }

  const stored = await loadSession(storage, state.mode, id, language);
  if (stored?.result) return stored;

  const won = state.status === 'won';
  const guessCount = state.guesses.length;
  const hintsUsed = state.hintsUsed;
  // A game's length is its answer's length (spec §10.1) — the XP table is
  // scaled by it, and nothing else in the session records it.
  const length = state.answer.length;
  const profile = await loadProfile(storage, language);

  let streak = profile.streak;
  let result: GameResult;
  /** The advanced ladder, written after the session result below (journey only). */
  let nextJourney: JourneyState | null = null;

  if (state.mode === 'daily') {
    if (typeof dayNumber !== 'number') {
      throw new Error('finalizeGame requires dayNumber for daily games');
    }
    // `id` and `dayNumber` are supplied separately by the caller; a
    // mismatch (e.g. a day-rollover race) would silently corrupt the
    // streak under someone else's day. Fail loudly instead.
    if (id !== String(dayNumber)) {
      throw new Error(
        `finalizeGame: daily id "${id}" does not match dayNumber ${dayNumber}`,
      );
    }
    streak = applyDailyOutcome(profile.streak, dayNumber, won);
    const streakAfter = streak.currentStreak;
    // Every number below comes out of core's XP table — never recomputed here.
    const xpAwarded = dailyXp(won, guessCount, streakAfter, hintsUsed, length);
    const xpBase = dailyXp(won, guessCount, 0, 0, length);
    const withBonus = dailyXp(won, guessCount, streakAfter, 0, length);
    result = {
      won,
      guessCount,
      xpAwarded,
      xpBase,
      xpStreakBonus: withBonus - xpBase,
      xpHintPenalty: withBonus - xpAwarded,
      streakAfter,
      finishedAt: now(),
    };
  } else if (state.mode === 'journey') {
    // The rung transition is core's; the storage and the bonus are the
    // web's, because the bonus depends on the run rather than the game.
    const before = await loadJourney(storage, language);
    const completed = journeyCompletedBy(before, won);
    const xpBase = journeyXp(won, guessCount, 0, length);
    const afterHints = journeyXp(won, guessCount, hintsUsed, length);
    const bonus = completed ? JOURNEY_COMPLETE_BONUS : 0;
    result = {
      won,
      guessCount,
      xpAwarded: afterHints + bonus,
      xpBase,
      xpStreakBonus: 0,
      xpHintPenalty: xpBase - afterHints,
      ...(bonus > 0 ? { xpJourneyBonus: bonus } : {}),
      // Journey never touches the streak (spec §10.3); report it unchanged.
      streakAfter: profile.streak.currentStreak,
      finishedAt: now(),
    };
    nextJourney = applyJourneyOutcome(before, won);
  } else {
    const xpAwarded = challengeXp(won, hintsUsed, length);
    const xpBase = challengeXp(won, 0, length);
    result = {
      won,
      guessCount,
      xpAwarded,
      xpBase,
      xpStreakBonus: 0,
      xpHintPenalty: xpBase - xpAwarded,
      // Challenges never touch the streak (spec §3.5); report it unchanged.
      streakAfter: profile.streak.currentStreak,
      finishedAt: now(),
    };
  }

  // Belt-and-braces: a non-finite award (e.g. from an impossible guessCount
  // indexing WIN_XP out of bounds) must never reach storage. JSON.stringify
  // silently turns NaN into `null`, which then fails loadProfile's shape
  // guard and resets the player's entire profile — a loud throw here beats
  // that silent wipe.
  if (!Number.isFinite(result.xpAwarded)) {
    throw new Error(`finalizeGame: computed non-finite xpAwarded (${result.xpAwarded})`);
  }

  // The stored result is the done-marker the "already has a result" check
  // above reads, so it must land BEFORE the journey/profile/stats writes it
  // authorizes: a process death mid-finalization then leaves a session the
  // next boot refuses to replay, losing at most this one game's award —
  // the old order (marker last) replayed the whole thing and double-paid
  // the XP, the streak, the stats and the rung. `revealedInsights` carries
  // forward so finalizing doesn't wipe reveals persisted during play (I4).
  const session = toSession(id, state, result, stored?.revealedInsights ?? []);
  await saveSession(storage, session, language);

  if (nextJourney) await saveJourney(storage, nextJourney, language);
  await saveProfile(storage, { totalXp: profile.totalXp + result.xpAwarded, streak }, language);
  await saveStats(storage, recordGame(await loadStats(storage, language), won, guessCount), language);
  return session;
}
